import React, { useEffect, useState } from "react";
import { useQuery } from "@apollo/client/react";
import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

import {
  LINK_LINKABLE_EXTERNAL_RECORD_MUTATION,
  LOOKUP_EXTERNAL_RECORD_MUTATION,
  UNLINK_LINKABLE_EXTERNAL_RECORD_MUTATION,
} from "src/constants/graphql/mutations";
import { LINKABLE_EXTERNAL_RECORDS_QUERY } from "src/constants/graphql/queries";
import useMutation from "src/hooks/useMutation";
import useNotification from "src/hooks/notification";
import useQueryParamModal from "src/hooks/useQueryParamModal";
import MovewareDiaryActionModal from "./MovewareDiaryActionModal";
import { optionFromRecord, presentDate, toIsoDate } from "./movewareActivityOptions";

export default function MovewareJobPicker({
  project,
  linkableType,
  linkableId,
  date,
  activityType,
  embedded = false,
  onLinked,
  onLinkStateChange,
  error,
}) {
  const { notifyError } = useNotification();
  const projectId = project?.id;
  const activityDate = toIsoDate(presentDate(date));
  const { isOpen, open: openModal, close: closeModal } = useQueryParamModal("linkMovewareDiaryAction");
  const [linkedSummary, setLinkedSummary] = useState(null);

  const { data: linkedData, loading, refetch: refetchLinked } = useQuery(LINKABLE_EXTERNAL_RECORDS_QUERY, {
    variables: { linkableType, linkableId },
    skip: !linkableType || !linkableId,
  });

  const [lookupExternalRecord, { loading: lookingUp }] = useMutation(LOOKUP_EXTERNAL_RECORD_MUTATION);
  const [linkExternalRecord, { loading: linking }] = useMutation(LINK_LINKABLE_EXTERNAL_RECORD_MUTATION);
  const [unlinkExternalRecord, { loading: unlinking }] = useMutation(UNLINK_LINKABLE_EXTERNAL_RECORD_MUTATION);

  const linkedRecord = (linkedData?.linkableExternalRecords || []).find(record => record.recordType === "job");
  const confirmedOption = optionFromRecord(linkedRecord);
  const summary =
    linkedSummary && linkedSummary.jobId === confirmedOption?.jobId ? linkedSummary : confirmedOption;
  const hasLink = Boolean(linkedRecord);
  const fieldValue = summary ? `Job ID: ${summary.jobNumber || summary.jobId}` : "";

  const [locateState, setLocateState] = useState({ lookupByChoice: false, backToList: null });

  const firstMessage = errors =>
    errors?.submit || Object.values(errors || {})[0] || "Could not update the Moveware job link. Please try again.";

  // The useMutation wrapper swallows network errors and resolves undefined, so
  // failures are reported through onFailure and every result is null-checked.
  const handleConfirm = async option => {
    if (!option?.jobId) {
      notifyError("This activity is not linked to a Moveware job.");
      return;
    }

    let failed = false;
    const onFailure = errors => {
      failed = true;
      notifyError(firstMessage(errors));
    };

    const lookup = await lookupExternalRecord({
      variables: { linkableType, linkableId, externalId: option.jobId },
      onFailure,
    });
    if (failed) return;

    const externalRecordId = lookup?.data?.lookupExternalRecord?.externalRecord?.id;
    if (!externalRecordId) {
      notifyError("Moveware did not return that job. Please try again.");
      return;
    }

    await linkExternalRecord({
      variables: { linkableType, linkableId, externalRecordId },
      onFailure,
    });
    if (failed) return;

    setLinkedSummary(option);
    try {
      await refetchLinked();
    } catch (error) {
      notifyError(error?.message || "Linked, but the page could not refresh. Reload to see the job.");
    }
    closeModal();
    onLinked?.();
  };

  const handleRemove = async () => {
    let failed = false;
    await unlinkExternalRecord({
      variables: { linkableType, linkableId },
      onFailure: errors => {
        failed = true;
        notifyError(firstMessage(errors));
      },
    });
    if (failed) return;

    setLinkedSummary(null);
    try {
      await refetchLinked();
    } catch (error) {
      notifyError(error?.message || "Removed, but the page could not refresh. Reload to see the change.");
    }
    closeModal();
    onLinked?.();
  };

  useEffect(() => {
    onLinkStateChange?.({
      loading,
      hasLink,
      confirming: lookingUp || linking,
      removing: unlinking,
      confirmJob: handleConfirm,
      removeJob: handleRemove,
      ...locateState,
    });
  }, [hasLink, linking, loading, locateState, lookingUp, onLinkStateChange, unlinking]);

  const modal = (
    <MovewareDiaryActionModal
      open={embedded || isOpen}
      embedded={embedded}
      projectId={projectId}
      date={activityDate}
      activityType={activityType}
      linkedOption={summary}
      confirming={lookingUp || linking}
      removing={unlinking}
      onClose={closeModal}
      onConfirm={handleConfirm}
      onRemove={handleRemove}
      onLocateState={setLocateState}
    />
  );

  if (embedded) {
    return (
      <Box>
        {error && (
          <Typography color="error" sx={{ mb: 1.5, fontWeight: 500 }}>
            {error}
          </Typography>
        )}
        {modal}
      </Box>
    );
  }

  return (
    <Box>
      <TextField
        fullWidth
        size="small"
        error={!!error}
        helperText={error}
        label="Link Moveware Diary Action"
        value={fieldValue}
        onClick={openModal}
        placeholder="Click to select"
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <ArrowDropDownIcon sx={{ color: "action.active" }} />
            </InputAdornment>
          ),
        }}
        sx={{ "& .MuiInputBase-root, & .MuiInputBase-input": { cursor: "pointer" } }}
      />
      {isOpen && modal}
    </Box>
  );
}
